<template>
  <v-app data-qa="doctor_service_prices">
    <slot v-if="!isMounted" />
    <template v-else>
      <div
        data-qa="doctor_service_prices_header"
        class="d-flex justify-space-between mb-3"
      >
        <h2
          v-if="headerText"
          data-qa="doctor_service_prices_header_text"
          class="text-h5 mr-4"
        >
          {{ headerText }}
        </h2>
        <LpuSelect
          v-if="lpuList.length > 1"
          v-model="selectedLpuId"
          :lpu-list="lpuList"
        />
        <div
          v-else-if="firstLpu"
          data-qa="doctor_service_prices_lpu"
          class="doctor-service-prices-lpu"
          :class="{
            'doctor-service-prices-lpu_desktop': !isMobile,
          }"
        >
          <div
            data-qa="doctor_service_prices_lpu_name"
            class="text-subtitle-1 text--text"
          >
            {{ transDynamic(firstLpu, `name,${currentLanguageIso},${needTranslate}`) }}
          </div>
          <div
            data-qa="doctor_service_prices_lpu_address"
            class="text-body-2 text-secondary--text"
          >
            {{ transDynamic(firstLpu, `address,${currentLanguageIso},${needTranslate}`) }}
          </div>
        </div>
      </div>
      <SearchCombobox
        class="mb-2"
        data-qa="doctor_services_search"
        :initial-data="serviceCategoryList"
        :placeholder="$t('Найти услугу', { ns: $i18next.language })"
        @search:default="handleSearchDefault"
        @search:text="handleSearchText"
        @search:class="handleSearchCategory"
      />
      <div
        class="mb-2"
        :class="{
          'd-flex justify-space-between align-center': !isMobile && isShowOnlineSwitch,
        }"
      >
        <OnlineSwitch
          v-if="isShowOnlineSwitch"
          class="my-2"
          :is-enabled-online-app="isEnabledOnlineApp"
          @online-switch:change="handleSwitchChange"
        />
        <UpdateInfo
          class="mb-8"
          :status="selectedFormattedServicePricesData.updateInfo.status"
          :date-string="selectedFormattedServicePricesData.updateInfo.dateString"
        />
      </div>
      <template
        v-if="isVisibleServiceList"
      >
        <PriceList
          class="d-flex flex-column"
          :category-list="searchData.categoryList"
          :is-show-more-button-visible="isShowMoreServicePricesButtonVisible"
          :show-more-button-text="showMoreServicePricesButtonText"
          @click:show-calendar="handleShowCalendar"
          @click:hide-calendar="handleHideCalendar"
          @click:show-more-services="handleClickShowMoreServices"
        />
      </template>
      <div
        v-else
        class="text-center py-16 my-1"
      >
        <img
          class="d-block mb-2 mx-auto"
          data-qa="doctor_services_stub"
          src="/static/_v1/pd/pictures/ui-kit/search.svg"
          width="150"
          height="150"
          alt="/"
        >
        <span class="text--text text-body-1">
          {{ $t('Услуга не найдена', { ns: $i18next.language }) }}
        </span>
      </div>
    </template>
  </v-app>
</template>

<script>
import {
  getFormattedPrice,
  dispatchCustomEventGlobally,
} from 'www/utils'
import { AppointmentModules } from 'www/modules/appointment-modules'
import YandexGoals from 'www/modules/YandexGoals'
import ScheduleVue from 'www/modules/Schedule/classes/ScheduleVue'
import { SCHEDULE_EVENTS } from 'www/modules/Schedule/constants'
import SearchCombobox from 'www/vue_components/common/core/SearchCombobox/SearchCombobox.vue'
import { getAgesText } from 'www/vue_components/common/DoctorServicePrices/functions'
import {
  getFormattedServicePricesData,
  getServicePricesDataList,
} from 'www/vue_components/common/DoctorServicePrices/interfaces'
import { postAppointmentButtonState } from 'www/vue_components/common/DoctorServicePrices/api'
import transDynamic from 'djanjucks/filters/transDynamic'
import {
  LpuSelect,
  PriceList,
  UpdateInfo,
  OnlineSwitch,
} from 'www/vue_components/common/DoctorServicePrices/components'
import {
  MIN_QUANTITY_SERVICES_WITH_ONLINE_APPOINTMENT,
} from 'www/vue_components/common/DoctorServicePrices/constants'
import declensionByNum from 'www/utils/executors/declensionByNum'

const instanceYaGoals = new YandexGoals()

export default {
  name: 'DoctorServicePrices',
  components: {
    LpuSelect,
    PriceList,
    UpdateInfo,
    OnlineSwitch,
    SearchCombobox,
  },
  props: {
    headerText: {
      type: String,
      default: '',
    },
    initSelectedLpuId: {
      type: Number,
      default: null,
    },
    servicePricesDataList: {
      type: Array,
      default: getServicePricesDataList,
    },
    initialServiceCategoryList: {
      type: Array,
      default: () => [],
    },
    initScheduleData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedLpuId: this.initSelectedLpuId || this.servicePricesDataList?.[0].lpu.id,
      firstLpu: this.servicePricesDataList?.[0].lpu,
      isMounted: false,
      isMobile: window.MOBILE_VERSION,
      searchData: {
        type: '',
        query: '',
        categoryList: [],
      },
      appointmentButtonData: {
        lpuId: null,
        doctorId: null,
        services: [],
      },
      isEnabledOnlineApp: false,
      currentServicePricePage: 1,
      showServicePricesPerPageNumber: window.MOBILE_VERSION ? 10 : 20,
      servicePricesNumberLeft: undefined,
      isAllServicePricesShown: false,
    }
  },
  computed: {
    currentLanguageIso() {
      return window.LANGUAGE_DATA.currentLanguage.iso
    },
    needTranslate() {
      return true
    },
    showMoreServicePricesButtonText() {
      const servicePricesNumberLeft = this.servicePricesNumberLeft || this.showServicePricesPerPageNumber
      const serviceWord = declensionByNum(servicePricesNumberLeft, ['услугу', 'услуги', 'услуг'])

      return `${this.$t('Показать ещё', { ns: this.$i18next.language })} ${servicePricesNumberLeft} ${this.$t(serviceWord, { ns: this.$i18next.language })}`
    },
    isShowMoreServicePricesButtonVisible() {
      return !this.isAllServicePricesShown && !this.searchData.query
    },
    formattedServiceCategoriesDataList() {
      return this.servicePricesDataList.map(servicePricesData => ({
        ...servicePricesData,
        categoryList: servicePricesData.serviceCategories?.map(serviceCategory => ({
          name: serviceCategory.name,
          priceList: serviceCategory.servicePrices.map(servicePrice => ({
            synonymId: servicePrice.synonymId,
            name: servicePrice.name,
            duration: servicePrice.duration,
            formattedPrice: getFormattedPrice(servicePrice.price),
            agesText: getAgesText(servicePrice.age),
            categoryName: servicePrice.categoryName,
            hasSlots: this.appointmentButtonData.services.includes(servicePrice.synonymId),
            isEnabledCalendar: false,
            isEnabledServiceDirect: servicePricesData.lpu.isServiceDirect,
          })),
        })),
      }))
    },
    selectedFormattedServicePricesData() {
      return this.formattedServiceCategoriesDataList.find(({ lpu }) => lpu.id === this.selectedLpuId)
        || getFormattedServicePricesData()
    },
    lpuList() {
      return this.servicePricesDataList.map(({ lpu }) => lpu)
    },
    isVisibleServiceList() {
      return this.searchData.categoryList.length
    },
    serviceCategoryList() {
      if (this.isEnabledOnlineApp) {
        return this
          .selectedFormattedServicePricesData
          .categoryList
          .map(i => {
            const priceList = i.priceList.filter(j => j.hasSlots && j.isEnabledServiceDirect)

            if (priceList.length) {
              return i.name
            }

            return undefined
          }).filter(i => i)
      }

      return this.selectedFormattedServicePricesData.categoryList.map(({ name }) => name)
    },
    selectedDoctorId() {
      try {
        return this.selectedFormattedServicePricesData.serviceCategories[0].servicePrices[0].doctorId // doctorId одинаков везде, поэтому можно выбирать из первого попавшегося списка
      } catch {
        return null
      }
    },
    selectedSynonymList() {
      try {
        return [
          ...new Set(
            this.selectedFormattedServicePricesData
              .serviceCategories.map(i => i.servicePrices.map(j => j.synonymId))
              .flat(Infinity),
          ),
        ]
      } catch {
        return []
      }
    },
    onlineAppointmentQuantity() {
      try {
        return this.selectedFormattedServicePricesData
          .categoryList
          .map(i => i.priceList.filter(j => j.hasSlots && j.isEnabledServiceDirect).length)
          .filter(i => i).reduce((total, item) => total + item, 0)
      } catch {
        return 0
      }
    },
    isShowOnlineSwitch() {
      return Boolean(this.onlineAppointmentQuantity)
    },
  },
  watch: {
    'searchData.query': function () {
      this.updateSearchData()
    },
    async selectedLpuId(val) {
      this.appointmentButtonData.lpuId = val

      await this.updateAppointmentButtonsState()

      this.updateSearchData()

      if (!this.isShowOnlineSwitch) {
        this.isEnabledOnlineApp = false
      }
    },
    isEnabledOnlineApp() {
      this.updateSearchData()
    },
  },
  async mounted() {
    await this.updateAppointmentButtonsState()

    new ScheduleVue({
      namespace: 'doctor-price-schedule',
    })

    window.addEventListener(SCHEDULE_EVENTS.renderSuccess, this.handleScheduleRenderSuccess)
    window.addEventListener(SCHEDULE_EVENTS.redirectSynonymPrice, this.handleRedirectAppointment)

    await this.$nextTick()

    this.updateSearchData()

    this.isEnabledOnlineApp = this.onlineAppointmentQuantity >= MIN_QUANTITY_SERVICES_WITH_ONLINE_APPOINTMENT
    this.isMounted = true
  },
  methods: {
    transDynamic,
    handleClickShowMoreServices() {
      this.currentServicePricePage += 1
      this.searchData.categoryList = this.getCategoryListForCurrentPage()
    },
    handleSwitchChange({ isEnabledOnline }) {
      instanceYaGoals.send({
        name: 'doctor-price-list-click-switch',
      })

      this.isEnabledOnlineApp = isEnabledOnline
    },
    handleShowCalendar({ synonymId, el }) {
      instanceYaGoals.send({
        name: 'doctor-price-list-click-button',
      })

      const payload = {
        lpuId: this.appointmentButtonData.lpuId,
        doctorId: this.appointmentButtonData.doctorId,
        dateStart: this.initScheduleData.dateStart,
        doctorsLpus: this.initScheduleData.doctorsLpus
          .filter(({ has_slots: hasSlots, lpu_id: lpuId }) => hasSlots && lpuId === this.appointmentButtonData.lpuId),
        townTimedelta: this.initScheduleData.townTimedelta,
        scheduleElements: {
          wrapperRowScheduleContainer: el,
        },
        synonymId,
        isEnabledDirect: true,
        isEnabledLpuClub: false,
        isDoctorPriceList: true,
        serviceData: {
          allowTreatmentReferral: false,
          requiresPrimaryConsultation: false,
          hasPrimaryConsultationDoctors: false,
        },
      }

      dispatchCustomEventGlobally(SCHEDULE_EVENTS.clickSynonymPrice, payload)
    },
    handleHideCalendar() {
      this.searchData.categoryList = this.searchData.categoryList.map(i => ({
        ...i,
        priceList: i.priceList.map(j => ({
          ...j,
          isEnabledCalendar: false,
        })),
      }))
    },
    handleSearchDefault() {
      this.searchData.type = ''
      this.searchData.query = ''
    },
    handleSearchText(query = '') {
      this.searchData.type = 'text'
      this.searchData.query = query
    },
    handleSearchCategory(query = '') {
      this.searchData.type = 'category'
      this.searchData.query = query
    },
    handleRedirectAppointment({ detail }) {
      const payload = {
        lpu: this.appointmentButtonData.lpuId,
        synonymId: detail.synonymId,
        targetDoctorId: this.appointmentButtonData.doctorId,
      }

      new AppointmentModules().redirectToNewAppointmentClinic(payload)
    },
    handleScheduleRenderSuccess({ detail }) {
      this.searchData.categoryList = this.searchData.categoryList.map(i => ({
        ...i,
        priceList: i.priceList.map(j => ({
          ...j,
          isEnabledCalendar: detail.synonymId === j.synonymId,
        })),
      }))
    },
    updateSearchData() {
      if (!this.searchData.query) {
        this.resetPaginationSettings()

        this.searchData.categoryList = this.getCategoryListForCurrentPage()

        return
      }

      this.searchData.categoryList = this.getSearchDataCategoryList()
    },
    resetPaginationSettings() {
      this.isAllServicePricesShown = false
      this.servicePricesNumberLeft = undefined
      this.currentServicePricePage = 1
    },
    getCategoryListForCurrentPage() {
      const categoryList = this.isEnabledOnlineApp
        ? this.getCategoryListWithOnlineApp()
        : this.selectedFormattedServicePricesData.categoryList

      let totalServicePricesNumber = 0
      let servicePricesAlreadyShown = 0

      const categoryListForCurrentPage = categoryList.map(category => {
        totalServicePricesNumber += category.priceList.length

        const servicePricesToShowNumber = (this.currentServicePricePage * this.showServicePricesPerPageNumber)
          - servicePricesAlreadyShown

        if (servicePricesToShowNumber === 0) {
          return
        }

        const isPriceListLongerThenNeedToShow = category.priceList.length >= servicePricesToShowNumber

        if (isPriceListLongerThenNeedToShow) {
          servicePricesAlreadyShown += servicePricesToShowNumber

          return {
            ...category,
            priceList: category.priceList.slice(0, servicePricesToShowNumber),
          }
        }

        servicePricesAlreadyShown += category.priceList.length

        return category
      })

      return categoryListForCurrentPage.filter(category => {
        if (!category) {
          return
        }

        // количество услуг, которые осталось показать
        const servicePricesToShowLeft = totalServicePricesNumber
            - (this.showServicePricesPerPageNumber * this.currentServicePricePage)

        if (servicePricesToShowLeft < this.showServicePricesPerPageNumber) {
          this.servicePricesNumberLeft = servicePricesToShowLeft
        }

        if (servicePricesToShowLeft < 0) {
          this.isAllServicePricesShown = true
        }

        return category
      })
    },
    getCategoryListWithOnlineApp() {
      return this
        .selectedFormattedServicePricesData
        .categoryList
        .map(i => {
          const priceList = i.priceList.filter(j => j.hasSlots && j.isEnabledServiceDirect)

          if (priceList.length) {
            return { ...i, priceList }
          }

          return undefined
        }).filter(i => i)
    },
    getSearchDataCategoryList() {
      const { type, query } = this.searchData

      if (!query.length) {
        if (this.isEnabledOnlineApp) {
          return this.getCategoryListWithOnlineApp()
        }

        return this.selectedFormattedServicePricesData.categoryList
      }

      const field = type === 'text' ? 'name' : 'categoryName'

      const categoryList = this.getSearchCategoryListResult({ field, query })

      if (categoryList.length) {
        return categoryList
      }

      const otherField = field === 'name' ? 'categoryName' : 'name' // Таким образом можно вводить категорию и искать по категории (не выбирать категории, а именно вводить) и наоборот

      return this.getSearchCategoryListResult({ field: otherField, query })
    },
    getSearchCategoryListResult({ field, query }) {
      const queryName = query.toLowerCase().trim()

      const result = this.selectedFormattedServicePricesData.categoryList.map(category => ({
        ...category,
        priceList: category.priceList.filter(
          ({ [field]: text }) => text && text.toLowerCase().trim().includes(queryName),
        ),
      }))
        .filter(({ priceList }) => priceList.length)

      if (this.isEnabledOnlineApp) {
        return result
          .map(i => {
            const priceList = i.priceList.filter(j => j.hasSlots && j.isEnabledServiceDirect)

            if (priceList.length) {
              return { ...i, priceList }
            }

            return undefined
          }).filter(i => i)
      }

      return result
    },
    async updateAppointmentButtonsState() {
      try {
        if (!(this.selectedDoctorId && this.selectedSynonymList?.length)) {
          return
        }

        const { data } = await postAppointmentButtonState([{
          lpu_id: this.selectedFormattedServicePricesData.lpu.id,
          doctors: [this.selectedDoctorId],
          services: this.selectedSynonymList,
        }])

        const { lpu_id: lpuId } = data[0]

        if (!data[0].doctors.length) {
          this.appointmentButtonData.services = []
          return
        }

        const { doctor_id: doctorId, services } = data[0].doctors[0]

        this.appointmentButtonData.lpuId = lpuId
        this.appointmentButtonData.doctorId = doctorId
        this.appointmentButtonData.services = services
      } catch { /* empty */ } // Не обрабатывается, поскольку если не получим состояние кнопок - не страшно. У пользователя могут быть иные целевые действия на странице
    },
  },
}
</script>

<style lang="scss">
.doctor-service-prices-lpu {
  &_desktop {
    max-width: 359px;
  }
}
</style>
